import React, { useState, useRef } from 'react';
import _get from 'lodash/get';
import { useData } from 'resift';
import withSource from 'components/withSource/withSource';
import useAnalytics from 'helpers/useAnalytics';
import MappingProps, * as MappingTypes from 'types/Mapping';
import SourceType from 'types/Source';
import useExistingOrgMappingsInfo from 'components/Mappings/OrgMapping/useExistingOrgMappingsInfo';
import MappingsContext from 'helpers/MappingsContext';
import makeGetMappingsAttributes from 'fetches/makeGetMappingsAttributes';
import {
  PRIMARY_MAPPING_TYPE_KEY,
  ORG_MAPPING_TYPE_KEY,
  PROFILE_ATTR_MAPPING_TYPE_KEY,
} from 'pages/Directory/consts';

import Card from '@material-ui/core/Card';
import PrimaryKeyMapping from 'components/Mappings/PrimaryKeyMapping';
import OrgMapping from 'components/Mappings/OrgMapping';
import ProfileAttrMapping from 'components/Mappings/ProfileAttrMapping';

import Directory from 'types/Directory';

interface Props {
  directory: Directory;
  cardData: MappingTypes.MappingCardData[];
  selectedDestinations: string[];
  onUpdateState: (cardIndex: number, newMappings: MappingProps[]) => void;
  sourceFromProp: SourceType;
}

function MappingCards({
  directory,
  cardData,
  selectedDestinations,
  onUpdateState,
  sourceFromProp,
}: Props) {
  const orgSectionRef = useRef(null);
  const profileAttrSectionRef = useRef(null);
  const getMappingsAttributes = makeGetMappingsAttributes(sourceFromProp.id);
  const mappingAttributes = useData(getMappingsAttributes);
  const analytics = useAnalytics();

  const existingMappingsInfo = useExistingOrgMappingsInfo(directory, sourceFromProp.id);
  const { orgMappingsSet } = existingMappingsInfo;
  const selectionAttributesArray: MappingTypes.SelectionAttribute[] = _get(
    mappingAttributes,
    'selectionAttributes',
    [],
  );
  const tlRefOptionsForCsvArray: MappingTypes.TlRefOption[] = _get(
    mappingAttributes,
    'tlRefOptions',
    [],
  );
  const _sourceAttrOptions = selectionAttributesArray.map(attr => attr.sourcePath);
  const tlRefOptionsForCsv = tlRefOptionsForCsvArray.map(option => option.sourcePath);
  const [sourceAttrOptions, setSourceAttrOptions] = useState(_sourceAttrOptions);
  const [accordionsExpandState, setAccordionsExpandState] = useState<Array<boolean>>([
    true,
    false,
    false,
  ]);

  const handleDeleteMapping = (cardIndex: number, mappings: MappingProps[]) => {
    return (indexToDelete: number) => {
      const newMappings = [...mappings];
      newMappings.splice(indexToDelete, 1);
      onUpdateState(cardIndex, newMappings);
    };
  };

  const handleUpdateMapping = (cardIndex: number, mappings: MappingProps[]) => {
    return (newMappingIndex: number, newMappingItems: MappingProps | MappingProps[]) => {
      if (Array.isArray(newMappingItems)) {
        onUpdateState(cardIndex, newMappingItems);
      } else {
        const newMappings = [...mappings];
        newMappings.splice(newMappingIndex, 1, newMappingItems);
        onUpdateState(cardIndex, newMappings);
      }
    };
  };

  const handleUpdateCard = (cardIndex: number) => {
    return (newMappings: MappingProps[]) => {
      onUpdateState(cardIndex, newMappings);
    };
  };

  const handleAddCustomSourceAttribute = (newOption: string) => {
    setSourceAttrOptions([...sourceAttrOptions, newOption]);
  };

  const mappingContext: MappingTypes.MappingContext = {
    source: sourceFromProp,
    selectedDestinations,
    sourceAttrOptions,
    tlRefOptionsForCsv,
    onAddCustomSourceAttribute: handleAddCustomSourceAttribute,
  };

  const refs = [null, orgSectionRef, profileAttrSectionRef];
  const handleScroll = (sectionIndex: number) => {
    refs[sectionIndex] &&
      // @ts-ignore
      refs[sectionIndex].current &&
      // @ts-ignore
      refs[sectionIndex].current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
  };

  return (
    <MappingsContext.Provider value={mappingContext}>
      {cardData.map((data, index) => {
        const { mappings, type } = data;
        const nextCardIndex = orgMappingsSet ? index + 2 : index + 1;

        const handleExpandNextSection = () => {
          const newAccordionsExpandState = [
            ...accordionsExpandState.slice(0, nextCardIndex),
            true,
            ...accordionsExpandState.slice(nextCardIndex + 1),
          ];

          setAccordionsExpandState(newAccordionsExpandState);
        };

        const analyticsLabels = ['primary-mapping', 'org-mapping'];

        const handleNextStep = async () => {
          handleExpandNextSection();
          handleScroll(nextCardIndex);

          analytics.track('Mappings - Next Step Button Clicked', {
            category: 'AD - Directories',
            label: analyticsLabels[index],
          });
        };

        const handleToggleExpansion = () => {
          const newAccordionsExpandState = [
            ...accordionsExpandState.slice(0, index),
            !accordionsExpandState[index],
            ...accordionsExpandState.slice(index + 1),
          ];

          setAccordionsExpandState(newAccordionsExpandState);
        };

        let CardComponent = null;

        switch (type) {
          case PRIMARY_MAPPING_TYPE_KEY:
            CardComponent = PrimaryKeyMapping;
            break;
          case ORG_MAPPING_TYPE_KEY:
            CardComponent = OrgMapping;
            break;
          case PROFILE_ATTR_MAPPING_TYPE_KEY:
            CardComponent = ProfileAttrMapping;
            break;
        }

        return (
          <div key={`mappings-card-${index}`} ref={refs[index]}>
            {!CardComponent ? null : (
              <Card style={{ marginBottom: 16, overflow: 'initial' }}>
                <CardComponent
                  mappings={mappings}
                  key={index}
                  onDeleteMapping={handleDeleteMapping(index, mappings)}
                  onUpdateMapping={handleUpdateMapping(index, mappings)}
                  onUpdateCard={handleUpdateCard(index)}
                  onNextStep={handleNextStep}
                  isExpanded={accordionsExpandState[index]}
                  onToggleExpansion={handleToggleExpansion}
                />
              </Card>
            )}
          </div>
        );
      })}
    </MappingsContext.Provider>
  );
}

export default withSource(MappingCards);
